import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, AlertCircle, BarChart3, Activity, CheckCircle2, FlaskConical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAccount } from '@/contexts/AccountContext'
import {
  getCampaignConversionOverview,
  getJourneyConversionOverview,
  type CampaignConversionItem,
  type JourneyConversionItem,
} from '../queries/attribution'
import { JourneySimulationPanel } from '../components/JourneySimulationPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

type EntityTab = 'campaigns' | 'journeys'
type OverviewModel = 'lastTouch' | 'linear'

const MODEL_LABELS: Record<OverviewModel, string> = {
  lastTouch: 'Last Touch',
  linear:    'Linear',
}

const CAMPAIGN_TYPE_COLORS: Record<string, string> = {
  email:    'bg-blue-50 text-blue-700',
  sms:      'bg-green-50 text-green-700',
  push:     'bg-orange-50 text-orange-700',
  whatsapp: 'bg-emerald-50 text-emerald-700',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  if (n == null) return '—'
  return n.toLocaleString('en-IN')
}

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—'
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 })
}

// ─── Shared subcomponents ─────────────────────────────────────────────────────

function StatCard({ label, value, accent }: { label: string; value: string; accent?: 'green' | 'gray' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className={cn(
        'mt-1 text-2xl font-bold truncate',
        accent === 'green' ? 'text-green-700' : 'text-gray-900',
      )}>{value}</p>
    </div>
  )
}

function ModelTabs({ model, onChange }: { model: OverviewModel; onChange: (m: OverviewModel) => void }) {
  return (
    <div className="flex items-center gap-1">
      {(Object.keys(MODEL_LABELS) as OverviewModel[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded transition-colors',
            model === m
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          )}
        >
          {MODEL_LABELS[m]}
        </button>
      ))}
    </div>
  )
}

function CampaignTypeBadge({ type }: { type: string }) {
  const cls = CAMPAIGN_TYPE_COLORS[type?.toLowerCase()] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded capitalize', cls)}>
      {type}
    </span>
  )
}

function DaysRemainingBadge({ days }: { days: number }) {
  const cls =
    days <= 2  ? 'bg-red-50 text-red-700 border-red-200' :
    days <= 7  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                 'bg-green-50 text-green-700 border-green-200'
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded border', cls)}>
      {days}d left
    </span>
  )
}

function SectionHeader({
  label,
  count,
  status,
}: {
  label: string
  count: number
  status: 'active' | 'closed'
}) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-4 py-2 border-b',
      status === 'active'
        ? 'bg-green-50 border-green-100'
        : 'bg-gray-50 border-gray-100',
    )}>
      {status === 'active'
        ? <Activity className="h-3.5 w-3.5 text-green-600" />
        : <CheckCircle2 className="h-3.5 w-3.5 text-gray-400" />}
      <span className={cn(
        'text-xs font-semibold uppercase tracking-wide',
        status === 'active' ? 'text-green-700' : 'text-gray-500',
      )}>
        {label}
      </span>
      <span className={cn(
        'text-xs px-1.5 py-0.5 rounded-full font-medium',
        status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600',
      )}>
        {count}
      </span>
    </div>
  )
}

// ─── Campaign Tab ─────────────────────────────────────────────────────────────

const CAMPAIGN_COLS = (
  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
    <th className="py-3 pl-4 pr-4">Campaign</th>
    <th className="py-3 pr-4">Conversion Event</th>
    <th className="py-3 pr-4">Window</th>
    <th className="py-3 pr-4 text-right">Status</th>
    <th className="py-3 pr-4 text-right">Conversions</th>
    <th className="py-3 pr-4 text-right">Revenue</th>
    <th className="py-3 pr-4 text-right">Days Calc.</th>
    <th className="py-3 pr-4 text-right">Last Calc.</th>
  </tr>
)

function CampaignRow({ item, model }: { item: CampaignConversionItem; model: OverviewModel }) {
  const conversions = model === 'lastTouch'
    ? item.attribution.lastTouchConversions
    : item.attribution.linearConversions
  const revenue = model === 'lastTouch'
    ? item.attribution.lastTouchRevenue
    : item.attribution.linearRevenue

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-3 pl-4 pr-4">
        <p className="font-medium text-gray-900 text-sm">{item.campaignName}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-400">#{item.campaignId}</span>
          <CampaignTypeBadge type={item.campaignType} />
        </div>
      </td>
      <td className="py-3 pr-4">
        <span className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
          {item.conversionEvent}
        </span>
        <p className="text-xs text-gray-400 mt-1">{item.conversionDurationDays}d window</p>
      </td>
      <td className="py-3 pr-4 whitespace-nowrap">
        <p className="text-xs font-mono text-gray-600">{item.windowStart}</p>
        <p className="text-xs font-mono text-gray-400">→ {item.windowEnd}</p>
      </td>
      <td className="py-3 pr-4 text-right">
        {item.daysRemaining > 0
          ? <DaysRemainingBadge days={item.daysRemaining} />
          : <span className="text-xs text-gray-400">Closed</span>}
      </td>
      <td className="py-3 pr-4 text-right">
        <span className="font-semibold text-gray-900 text-sm">{fmt(conversions)}</span>
      </td>
      <td className="py-3 pr-4 text-right">
        <span className="font-semibold text-emerald-700 text-sm">{fmtCurrency(revenue)}</span>
      </td>
      <td className="py-3 pr-4 text-right text-sm text-gray-500">
        {item.attribution.daysCalculated}d
      </td>
      <td className="py-3 pr-4 text-right">
        <span className="text-xs font-mono text-gray-500">{item.attribution.lastAttributionDate}</span>
      </td>
    </tr>
  )
}

function CampaignOverviewTable({
  active,
  closed,
  model,
  isLoading,
}: {
  active: CampaignConversionItem[]
  closed: CampaignConversionItem[]
  model: OverviewModel
  isLoading: boolean
}) {
  if (isLoading) {
    return <div className="p-12 text-center text-gray-400 text-sm">Loading campaigns…</div>
  }
  if (active.length === 0 && closed.length === 0) {
    return <div className="p-12 text-center text-gray-400 text-sm">No campaign attribution data found.</div>
  }

  return (
    <div className="overflow-x-auto">
      {active.length > 0 && (
        <>
          <SectionHeader label="Active" count={active.length} status="active" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">{CAMPAIGN_COLS}</thead>
            <tbody>
              {active.map((c) => <CampaignRow key={c.campaignId} item={c} model={model} />)}
            </tbody>
          </table>
        </>
      )}
      {closed.length > 0 && (
        <>
          <SectionHeader label="Closed" count={closed.length} status="closed" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">{CAMPAIGN_COLS}</thead>
            <tbody>
              {closed.map((c) => <CampaignRow key={c.campaignId} item={c} model={model} />)}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ─── Journey Tab ──────────────────────────────────────────────────────────────

const JOURNEY_COLS = (
  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
    <th className="py-3 pl-4 pr-4">Journey</th>
    <th className="py-3 pr-4">Conversion Event</th>
    <th className="py-3 pr-4">Window</th>
    <th className="py-3 pr-4 text-right">Status</th>
    <th className="py-3 pr-4 text-right">Conversions</th>
    <th className="py-3 pr-4 text-right">Revenue</th>
    <th className="py-3 pr-4 text-right">Days Calc.</th>
    <th className="py-3 pr-4 text-right">Last Calc.</th>
    <th className="py-3 pr-4"></th>
  </tr>
)

function JourneyRow({ item, model }: { item: JourneyConversionItem; model: OverviewModel }) {
  const [expanded, setExpanded] = useState(false)

  const conversions = model === 'lastTouch'
    ? item.attribution.lastTouchConversions
    : item.attribution.linearConversions
  const revenue = model === 'lastTouch'
    ? item.attribution.lastTouchRevenue
    : item.attribution.linearRevenue

  return (
    <>
      <tr className={cn(
        'hover:bg-gray-50 transition-colors',
        expanded ? 'bg-indigo-50/30' : 'border-b border-gray-100',
      )}>
        <td className="py-3 pl-4 pr-4">
          <p className="font-medium text-gray-900 text-sm">{item.journeyName}</p>
          <p className="text-xs text-gray-400 mt-0.5">{item.journeyCode}</p>
        </td>
        <td className="py-3 pr-4">
          <span className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
            {item.conversionEvent}
          </span>
          <p className="text-xs text-gray-400 mt-1">{item.conversionDurationDays}d window</p>
        </td>
        <td className="py-3 pr-4 whitespace-nowrap">
          <p className="text-xs font-mono text-gray-600">{item.windowStart}</p>
          <p className="text-xs font-mono text-gray-400">→ {item.windowEnd}</p>
        </td>
        <td className="py-3 pr-4 text-right">
          {item.daysRemaining > 0
            ? <DaysRemainingBadge days={item.daysRemaining} />
            : <span className="text-xs text-gray-400">Closed</span>}
        </td>
        <td className="py-3 pr-4 text-right">
          <span className="font-semibold text-gray-900 text-sm">{fmt(conversions)}</span>
        </td>
        <td className="py-3 pr-4 text-right">
          <span className="font-semibold text-emerald-700 text-sm">{fmtCurrency(revenue)}</span>
        </td>
        <td className="py-3 pr-4 text-right text-sm text-gray-500">
          {item.attribution.daysCalculated}d
        </td>
        <td className="py-3 pr-4 text-right">
          <span className="text-xs font-mono text-gray-500">{item.attribution.lastAttributionDate}</span>
        </td>
        <td className="py-3 pr-4 text-right">
          <button
            onClick={() => setExpanded(e => !e)}
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors',
              expanded
                ? 'bg-indigo-200 text-indigo-800'
                : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50',
            )}
          >
            <FlaskConical className="h-3.5 w-3.5" />
            Simulate
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-gray-100">
          <td colSpan={9} className="p-0">
            <JourneySimulationPanel item={item} onClose={() => setExpanded(false)} />
          </td>
        </tr>
      )}
    </>
  )
}

function JourneyOverviewTable({
  active,
  closed,
  model,
  isLoading,
}: {
  active: JourneyConversionItem[]
  closed: JourneyConversionItem[]
  model: OverviewModel
  isLoading: boolean
}) {
  if (isLoading) {
    return <div className="p-12 text-center text-gray-400 text-sm">Loading journeys…</div>
  }
  if (active.length === 0 && closed.length === 0) {
    return <div className="p-12 text-center text-gray-400 text-sm">No journey attribution data found.</div>
  }

  return (
    <div className="overflow-x-auto">
      {active.length > 0 && (
        <>
          <SectionHeader label="Active" count={active.length} status="active" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">{JOURNEY_COLS}</thead>
            <tbody>
              {active.map((j) => <JourneyRow key={j.journeyId} item={j} model={model} />)}
            </tbody>
          </table>
        </>
      )}
      {closed.length > 0 && (
        <>
          <SectionHeader label="Closed" count={closed.length} status="closed" />
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">{JOURNEY_COLS}</thead>
            <tbody>
              {closed.map((j) => <JourneyRow key={j.journeyId} item={j} model={model} />)}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AttributionReportPage() {
  const [entityTab, setEntityTab] = useState<EntityTab>('campaigns')
  const [model, setModel] = useState<OverviewModel>('lastTouch')

  const { selectedAccountId } = useAccount()
  const accId =
    selectedAccountId && selectedAccountId !== 'ALL'
      ? parseInt(selectedAccountId, 10)
      : undefined

  const campaignQuery = useQuery({
    queryKey: ['attribution-campaign-overview', accId],
    queryFn: () => getCampaignConversionOverview(accId!),
    enabled: accId != null,
    staleTime: 60_000,
  })

  const journeyQuery = useQuery({
    queryKey: ['attribution-journey-overview', accId],
    queryFn: () => getJourneyConversionOverview(accId!),
    enabled: accId != null,
    staleTime: 60_000,
  })

  const campaignActive = campaignQuery.data?.active ?? []
  const campaignClosed = campaignQuery.data?.closed ?? []
  const journeyActive  = journeyQuery.data?.active  ?? []
  const journeyClosed  = journeyQuery.data?.closed  ?? []

  const isFetching = campaignQuery.isFetching || journeyQuery.isFetching
  const error      = campaignQuery.error ?? journeyQuery.error

  function refetchAll() {
    campaignQuery.refetch()
    journeyQuery.refetch()
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Conversion Reports</h1>
            <p className="text-sm text-gray-500">Campaign & journey-level multi-touch conversion tracking</p>
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={refetchAll} disabled={isFetching}>
          <RefreshCw className={cn('h-4 w-4 mr-1.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Account not selected */}
      {accId == null && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Select an account from the header to view attribution reports.
        </div>
      )}

      {/* Stats */}
      {accId != null && (campaignQuery.data || journeyQuery.data) && (
        <div className="flex gap-4 flex-wrap">
          <StatCard
            label="Active Campaigns"
            value={fmt(campaignQuery.data?.activeCount)}
            accent="green"
          />
          <StatCard
            label="Closed Campaigns"
            value={fmt(campaignQuery.data?.closedCount)}
          />
          <StatCard
            label="Active Journeys"
            value={fmt(journeyQuery.data?.activeCount)}
            accent="green"
          />
          <StatCard
            label="Closed Journeys"
            value={fmt(journeyQuery.data?.closedCount)}
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Main card */}
      {accId != null && !error && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

          {/* Tab bar */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4">
            <div className="flex">
              {(['campaigns', 'journeys'] as EntityTab[]).map((tab) => {
                const count = tab === 'campaigns'
                  ? campaignActive.length + campaignClosed.length
                  : journeyActive.length + journeyClosed.length
                return (
                  <button
                    key={tab}
                    onClick={() => setEntityTab(tab)}
                    className={cn(
                      'px-5 py-3.5 text-sm font-medium border-b-2 transition-colors capitalize',
                      entityTab === tab
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700',
                    )}
                  >
                    {tab}{count > 0 ? ` (${count})` : ''}
                  </button>
                )
              })}
            </div>

            <div className="flex items-center gap-2 py-2">
              <span className="text-xs text-gray-400 mr-1">Model:</span>
              <ModelTabs model={model} onChange={setModel} />
            </div>
          </div>

          {entityTab === 'campaigns' && (
            <CampaignOverviewTable
              active={campaignActive}
              closed={campaignClosed}
              model={model}
              isLoading={campaignQuery.isLoading}
            />
          )}
          {entityTab === 'journeys' && (
            <JourneyOverviewTable
              active={journeyActive}
              closed={journeyClosed}
              model={model}
              isLoading={journeyQuery.isLoading}
            />
          )}
        </div>
      )}
    </div>
  )
}
